import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  LogOut,
  Trash2,
  Mail,
  Phone,
  Loader2,
  Inbox,
  Users,
  Send,
  X,
  UserPlus,
  ShieldCheck,
  Newspaper,
  Pencil,
  ImagePlus,
  Megaphone,
  Copy,
  ExternalLink,
  Check,
  Download,
  Images,
  Quote,
  Settings,
  LayoutDashboard,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { api, formatApiErrorDetail } from "./api";
import { setPageMeta } from "../seo";
import OverviewTab from "./tabs/OverviewTab";
import GalleryTab from "./tabs/GalleryTab";
import TestimonialsTab from "./tabs/TestimonialsTab";
import SettingsTab from "./tabs/SettingsTab";
import TeamTab from "./tabs/TeamTab";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const map = {
    new: "bg-orange-100 text-orange-800",
    in_progress: "bg-sky-100 text-sky-800",
    replied: "bg-green-100 text-green-800",
    closed: "bg-stone-200 text-stone-700",
  };
  const labels = { new: "New", in_progress: "In progress", replied: "Replied", closed: "Closed" };
  return (
    <span className={`rounded-full px-3 py-1 text-sm font-medium ${map[status] || "bg-stone-100 text-stone-700"}`}>
      {labels[status] || status}
    </span>
  );
};

function ReplyModal({ enquiry, onClose, onSent }) {
  const isEmail = (enquiry.contact_detail || "").includes("@");
  const [subject, setSubject] = useState(`Re: your enquiry to Spire Inclusion`);
  const [message, setMessage] = useState(`Hello ${enquiry.name},\n\nThank you for your enquiry.\n\n`);
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post(`/admin/enquiries/${enquiry.id}/reply`, { subject, message });
      toast.success(res.data?.message || "Reply sent.");
      onSent();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 p-4" role="dialog" aria-label="Reply to enquiry">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg border border-stone-200 p-6">
        <div className="flex items-start justify-between">
          <h2 className="font-heading text-xl font-semibold text-stone-900">Reply to {enquiry.name}</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-stone-500 hover:bg-stone-100" aria-label="Close" data-testid="reply-close-button">
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-1 text-base text-stone-600">To: {enquiry.contact_detail}</p>

        {!isEmail ? (
          <div className="mt-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 text-base text-stone-800">
            This enquirer gave a phone number, not an email address, so a reply email cannot be sent. Please call them on {enquiry.contact_detail}.
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="reply-subject" className="block text-base font-medium text-stone-900 mb-1">Subject</label>
              <input
                id="reply-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 text-base focus-visible:border-orange-600"
                data-testid="reply-subject-input"
              />
            </div>
            <div>
              <label htmlFor="reply-message" className="block text-base font-medium text-stone-900 mb-1">Message</label>
              <textarea
                id="reply-message"
                rows={7}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-xl border-2 border-stone-300 px-3 py-2 text-base focus-visible:border-orange-600"
                data-testid="reply-message-input"
              />
            </div>
            <button
              onClick={send}
              disabled={sending}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70"
              data-testid="reply-send-button"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EnquiryNotes({ enquiry, onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/notes`, { text });
      setText("");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-stone-100 pt-3">
      {Array.isArray(enquiry.notes) && enquiry.notes.length > 0 && (
        <div className="space-y-2 mb-3">
          {enquiry.notes.map((n, i) => (
            <div key={i} className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm text-stone-700" data-testid={`enquiry-note-${enquiry.id}-${i}`}>
              <p className="whitespace-pre-wrap">{n.text}</p>
              <p className="mt-1 text-xs text-stone-400">Note by {n.by} · {fmtDate(n.at)}</p>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={add} className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a private note (only staff can see this)"
          aria-label="Add a private note"
          className="h-10 w-full rounded-lg border-2 border-stone-200 px-3 text-sm focus-visible:border-orange-600"
          data-testid={`enquiry-note-input-${enquiry.id}`}
        />
        <button type="submit" disabled={saving || !text.trim()} className="px-4 rounded-lg bg-stone-800 text-white text-sm font-semibold hover:bg-stone-900 disabled:opacity-40" data-testid={`enquiry-note-add-${enquiry.id}`}>
          {saving ? "…" : "Add note"}
        </button>
      </form>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "replied", label: "Replied" },
  { value: "closed", label: "Closed" },
];

function EnquiriesTab() {
  const [items, setItems] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(() => {
    api
      .get("/admin/enquiries")
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/enquiries/${id}/status`, { status });
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/enquiries/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "spire-enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Enquiries exported.");
    } catch {
      toast.error("Could not export enquiries.");
    }
  };

  if (items === null)
    return (
      <div className="flex items-center gap-2 text-stone-600 py-10">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading enquiries…
      </div>
    );

  if (items.length === 0)
    return (
      <div className="text-center py-16">
        <Inbox className="mx-auto h-12 w-12 text-stone-400" aria-hidden="true" />
        <p className="mt-3 text-lg text-stone-600">No enquiries yet.</p>
      </div>
    );

  const types = [...new Set(items.map((e) => e.enquiry_type).filter(Boolean))];
  const filtered = items.filter(
    (e) => (statusFilter === "all" || e.status === statusFilter) && (typeFilter === "all" || e.enquiry_type === typeFilter)
  );

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-lg border-2 border-stone-300 px-2 text-sm bg-white focus-visible:border-orange-600" data-testid="enquiry-status-filter">
            <option value="all">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
          Type
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-10 rounded-lg border-2 border-stone-300 px-2 text-sm bg-white focus-visible:border-orange-600" data-testid="enquiry-type-filter">
            <option value="all">All</option>
            {types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <button onClick={exportCsv} className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-stone-300 text-stone-700 text-sm font-semibold hover:border-orange-600 hover:bg-orange-50" data-testid="enquiry-export-button">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {filtered.length === 0 && <p className="text-stone-600 py-6">No enquiries match those filters.</p>}

      <div className="space-y-4" data-testid="enquiries-list">
        {filtered.map((e) => (
          <div key={e.id} className="rounded-2xl bg-white border border-stone-200 p-5 sm:p-6" data-testid={`enquiry-row-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-heading text-lg font-semibold text-stone-900">{e.name}</h3>
                  <StatusBadge status={e.status} />
                </div>
                <p className="mt-1 text-sm text-stone-500">{fmtDate(e.created_at)} · {e.enquiry_type} · {e.relationship}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  aria-label="Change enquiry status"
                  className="h-9 rounded-lg border-2 border-stone-300 px-2 text-sm bg-white focus-visible:border-orange-600"
                  data-testid={`enquiry-status-select-${e.id}`}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => setReplyTo(e)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700"
                  data-testid={`reply-button-${e.id}`}
                >
                  <Send className="h-4 w-4" /> Reply
                </button>
                <button
                  onClick={() => remove(e.id)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-stone-300 text-stone-700 text-sm font-semibold hover:border-red-400 hover:text-red-700"
                  data-testid={`delete-button-${e.id}`}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
            <p className="mt-3 text-base text-stone-700 whitespace-pre-wrap">{e.message}</p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-stone-600">
              <span className="inline-flex items-center gap-1.5">
                {e.contact_detail.includes("@") ? <Mail className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                {e.contact_detail}
              </span>
              <span>Prefers: {e.preferred_contact}</span>
            </div>
            {Array.isArray(e.replies) && e.replies.length > 0 && (
              <div className="mt-4 border-t border-stone-100 pt-3 space-y-2">
                {e.replies.map((r, i) => (
                  <div key={i} className="rounded-lg bg-stone-50 p-3 text-sm text-stone-600">
                    <p className="font-medium text-stone-800">{r.subject}</p>
                    <p className="whitespace-pre-wrap">{r.message}</p>
                    <p className="mt-1 text-xs text-stone-400">Sent by {r.sent_by} · {fmtDate(r.sent_at)}</p>
                  </div>
                ))}
              </div>
            )}
            <EnquiryNotes enquiry={e} onChanged={load} />
          </div>
        ))}
      </div>
      {replyTo && (
        <ReplyModal
          enquiry={replyTo}
          onClose={() => setReplyTo(null)}
          onSent={() => {
            setReplyTo(null);
            load();
          }}
        />
      )}
    </>
  );
}

function StaffTab({ currentUser }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get("/auth/users").then((res) => setUsers(res.data)).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/auth/users", form);
      toast.success("Staff login created.");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff login?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Staff login removed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Staff logins</h3>
        <div className="mt-4 space-y-3" data-testid="staff-list">
          {users === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
          ) : (
            users.map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-xl bg-white border border-stone-200 p-4" data-testid={`staff-row-${u.id}`}>
                <div>
                  <p className="font-medium text-stone-900 flex items-center gap-2">
                    {u.name || u.email}
                    {u.role === "admin" && <ShieldCheck className="h-4 w-4 text-orange-600" aria-label="Admin" />}
                  </p>
                  <p className="text-sm text-stone-500">{u.email}</p>
                </div>
                {u.id !== currentUser.id && (
                  <button
                    onClick={() => remove(u.id)}
                    className="rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-700"
                    aria-label={`Remove ${u.email}`}
                    data-testid={`staff-delete-${u.id}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Add a staff login</h3>
        <form onSubmit={add} className="mt-4 space-y-4 rounded-2xl bg-white border border-stone-200 p-5">
          <div>
            <label htmlFor="staff-name" className="block text-base font-medium text-stone-900 mb-1">Name</label>
            <input id="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="staff-name-input" />
          </div>
          <div>
            <label htmlFor="staff-email" className="block text-base font-medium text-stone-900 mb-1">Email</label>
            <input id="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="staff-email-input" />
          </div>
          <div>
            <label htmlFor="staff-password" className="block text-base font-medium text-stone-900 mb-1">Temporary password</label>
            <input id="staff-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="staff-password-input" />
          </div>
          <button type="submit" disabled={adding} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="staff-add-button">
            {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-5 w-5" />}
            Add login
          </button>
        </form>
      </div>
    </div>
  );
}

function NewsTab() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", published: true });
  const [file, setFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/news").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setForm({ title: "", body: "", published: true });
    setFile(null);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("body", form.body);
      fd.append("published", form.published ? "true" : "false");
      if (file) fd.append("image", file);
      if (editingId) await api.put(`/admin/news/${editingId}`, fd);
      else await api.post(`/admin/news`, fd);
      toast.success(editingId ? "Post updated." : "Post published.");
      reset();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({ title: p.title, body: p.body, published: p.published });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success("Post deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">
          {editingId ? "Edit post" : "Add a news post"}
        </h3>
        <form onSubmit={submit} className="mt-4 space-y-4 rounded-2xl bg-white border border-stone-200 p-5">
          <div>
            <label htmlFor="news-title" className="block text-base font-medium text-stone-900 mb-1">Title</label>
            <input id="news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="news-title-input" />
          </div>
          <div>
            <label htmlFor="news-body" className="block text-base font-medium text-stone-900 mb-1">Body</label>
            <textarea id="news-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required className="w-full rounded-xl border-2 border-stone-300 px-3 py-2 focus-visible:border-orange-600" data-testid="news-body-input" />
          </div>
          <div>
            <label htmlFor="news-image" className="block text-base font-medium text-stone-900 mb-1">
              Photo {editingId && "(leave empty to keep current)"}
            </label>
            <input id="news-image" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => setFile(e.target.files[0] || null)} className="block w-full text-base file:mr-3 file:rounded-lg file:border-0 file:bg-orange-600 file:px-4 file:py-2 file:text-white file:font-semibold" data-testid="news-image-input" />
          </div>
          <label className="flex items-center gap-2 text-base text-stone-800">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-5 w-5 accent-orange-600" data-testid="news-published-checkbox" />
            Published (visible on the website)
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="news-save-button">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
              {editingId ? "Update post" : "Publish post"}
            </button>
            {editingId && (
              <button type="button" onClick={reset} className="px-5 py-2.5 rounded-xl border-2 border-stone-300 font-semibold hover:bg-stone-100" data-testid="news-cancel-button">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Posts</h3>
        <div className="mt-4 space-y-3" data-testid="news-admin-list">
          {posts === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
          ) : posts.length === 0 ? (
            <p className="text-stone-600">No posts yet.</p>
          ) : (
            posts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl bg-white border border-stone-200 p-3" data-testid={`news-admin-row-${p.id}`}>
                {p.image_url ? (
                  <img src={`${BACKEND}${p.image_url}`} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-stone-100 text-stone-400 shrink-0">
                    <Newspaper className="h-6 w-6" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-stone-900 truncate">{p.title}</p>
                  <p className="text-sm text-stone-500">{p.published ? "Published" : "Draft"}</p>
                </div>
                <button onClick={() => edit(p)} className="rounded-lg p-2 text-stone-500 hover:bg-orange-50 hover:text-orange-700" aria-label={`Edit ${p.title}`} data-testid={`news-edit-${p.id}`}>
                  <Pencil className="h-5 w-5" />
                </button>
                <button onClick={() => remove(p.id)} className="rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-700" aria-label={`Delete ${p.title}`} data-testid={`news-delete-${p.id}`}>
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function RecruitmentTab() {
  const FB_URL = "https://www.facebook.com/share/1Ef8aKzjKk/";
  const DEFAULT_POST = `📢 We're recruiting at Spire Inclusion!

We're looking for warm, patient and caring people to join our team in Salisbury, supporting young adults with disabilities to build confidence, independence and connection.

✨ Meaningful, rewarding work
🤝 A friendly, values-led team
📚 Full training and support provided
🕐 Weekday hours (Monday to Friday)

No experience needed — what matters most is a caring, can-do attitude.

Interested? Visit spireinclusion.co.uk/careers or send us a message to find out more. We'd love to hear from you! 💛`;

  const [recruiting, setRecruiting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState(DEFAULT_POST);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get("/settings/recruitment")
      .then((r) => setRecruiting(!!r.data.recruiting))
      .catch((err) => {
        setRecruiting(false);
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      });
  }, []);

  const toggle = async () => {
    const next = !recruiting;
    setSaving(true);
    try {
      await api.put("/admin/settings/recruitment", { recruiting: next });
      setRecruiting(next);
      toast.success(next ? "Recruitment is now OPEN on the website." : "Recruitment is now CLOSED on the website.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copyPost = async () => {
    try {
      await navigator.clipboard.writeText(post);
      setCopied(true);
      toast.success("Post copied to clipboard — paste it into Facebook.");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Could not copy automatically. Please select the text and copy it manually.");
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Recruitment status</h3>
        <div className="mt-4 rounded-2xl bg-white border border-stone-200 p-6" data-testid="recruitment-toggle-card">
          {recruiting === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
          ) : (
            <>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-lg font-semibold text-stone-900">
                    Recruitment is{" "}
                    <span className={recruiting ? "text-green-700" : "text-red-700"}>
                      {recruiting ? "Open" : "Closed"}
                    </span>
                  </p>
                  <p className="mt-1 text-base text-stone-600">
                    This controls the banner and wording on the Careers page.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={recruiting}
                  aria-label="Toggle recruitment open or closed"
                  onClick={toggle}
                  disabled={saving}
                  className={`relative h-8 w-14 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
                    recruiting ? "bg-green-600" : "bg-stone-300"
                  }`}
                  data-testid="recruitment-toggle-switch"
                >
                  <span
                    className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${
                      recruiting ? "left-7" : "left-1"
                    }`}
                  />
                </button>
              </div>
              <div className={`mt-4 rounded-xl p-4 text-base ${recruiting ? "bg-green-50 text-green-900" : "bg-stone-50 text-stone-700"}`}>
                {recruiting
                  ? "Visitors to the Careers page will see \"Recruitment: Open\" and be invited to enquire about roles."
                  : "Visitors to the Careers page will see \"Recruitment: Closed\" and be invited to register their interest instead."}
              </div>
            </>
          )}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Facebook recruitment post</h3>
        <div className="mt-4 rounded-2xl bg-white border border-stone-200 p-6 space-y-4" data-testid="recruitment-fb-card">
          <p className="text-base text-stone-600">
            Edit the post below, copy it, then paste it into your Spire Inclusion Facebook page.
            Once you have a Meta Page Access Token, this can be upgraded to post automatically.
          </p>
          <textarea
            rows={14}
            value={post}
            onChange={(e) => setPost(e.target.value)}
            className="w-full rounded-xl border-2 border-stone-300 px-3 py-2 text-base focus-visible:border-orange-600"
            aria-label="Facebook recruitment post text"
            data-testid="recruitment-fb-textarea"
          />
          <div className="flex flex-wrap gap-3">
            <button
              onClick={copyPost}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700"
              data-testid="recruitment-fb-copy-button"
            >
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              {copied ? "Copied!" : "Copy post"}
            </button>
            <a
              href={FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-stone-300 font-semibold text-stone-700 hover:border-orange-600 hover:bg-orange-50"
              data-testid="recruitment-fb-open-link"
            >
              <ExternalLink className="h-5 w-5" /> Open Facebook
            </a>
            <button
              onClick={() => setPost(DEFAULT_POST)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-stone-600 font-semibold hover:bg-stone-100"
              data-testid="recruitment-fb-reset-button"
            >
              Reset text
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    setPageMeta({
      title: "Admin Dashboard | Spire Inclusion",
      description: "Spire Inclusion staff admin dashboard.",
      path: "/admin",
      noindex: true,
    });
  }, []);

  const tabBtn = (id, label, Icon) => (
    <button
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
        tab === id ? "bg-orange-600 text-white" : "bg-white text-stone-700 border border-stone-200 hover:bg-orange-50"
      }`}
      data-testid={`tab-${id}`}
    >
      <Icon className="h-5 w-5" /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-stone-100">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 text-white font-heading font-bold">S</span>
            <div>
              <p className="font-heading font-bold text-stone-900 leading-tight">Spire Inclusion</p>
              <p className="text-sm text-stone-500 leading-tight">Admin dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-stone-600">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-stone-300 text-stone-700 font-semibold hover:border-orange-600 hover:bg-orange-50" data-testid="logout-button">
              <LogOut className="h-5 w-5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-3 mb-6">
          {tabBtn("overview", "Overview", LayoutDashboard)}
          {tabBtn("enquiries", "Enquiries", Inbox)}
          {tabBtn("news", "News", Newspaper)}
          {tabBtn("gallery", "Gallery", Images)}
          {tabBtn("testimonials", "Testimonials", Quote)}
          {tabBtn("team", "Team", Users)}
          {tabBtn("recruitment", "Recruitment", Megaphone)}
          {tabBtn("settings", "Settings", Settings)}
          {tabBtn("staff", "Staff logins", Users)}
        </div>
        {tab === "overview" && <OverviewTab onNavigate={setTab} />}
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "news" && <NewsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "team" && <TeamTab />}
        {tab === "recruitment" && <RecruitmentTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "staff" && <StaffTab currentUser={user} />}
      </main>
    </div>
  );
}
